import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { Permissions } from '@app/infrastructure/permission/permission.model';
import { Organization } from '@app/interfaces/accounts_list';
import { OrganizationsService } from '@app/services/organizations.service';
import { UserService } from '@app/services/user/user.service';
import { CONTROL_PANEL_KEY, sidebarSections } from '@app/sidebar-layout/config/sidebar.config';
import { SectionChapterEnum } from '@app/sidebar-layout/enums/section-chapter.enum';
import { ISection } from '@app/sidebar-layout/interfaces/i-section';
import { ISectionChapter } from '@app/sidebar-layout/interfaces/i-section-chapter';
import { ISectionPage } from '@app/sidebar-layout/interfaces/i-section-page';
import { ISidebarRestrictions } from '@app/sidebar-layout/interfaces/i-sidebar-restrictions';
import { SupportModalService } from '@app/support-request/support-modal-component/support-modal.service';
import { AccountService } from '@core/services/account/account.service';
import { OrganizationTypes } from '@core/services/organization/organization.interface';
import { PermissionService } from '@core/services/permission/permission.service';
import { UnsubscriberBase } from '@core/services/unsubscriber.base';
import { getActiveOrganization } from '@utils/helpers';
import { takeUntil } from 'rxjs/operators';
import { WebComponentWrapperOptions } from '@angular-architects/module-federation-tools';
import { APP_CONFIG, AppConfiguration } from '@app/infrastructure/auth/app-configuration';
import { CustomerType } from '@partners/customer-type.enum';

@Component({
	selector: 'app-sidebar-layout',
	templateUrl: './sidebar-layout.component.html',
	styleUrls: ['./sidebar-layout.component.scss'],
})
export class SidebarLayoutComponent extends UnsubscriberBase implements OnInit {
	@FeatureFlag(FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB) enableOptimizationHub: boolean;

	@HostBinding('class.sidebar-collapsed') private _isSidebarCollapsed: boolean = document.body.clientWidth < 991;
	private _restrictions: ISidebarRestrictions = {
		isOrganizationPickerAllowed: false,
		isPresentationModeAllowed: false,
		isNotificationsAllowed: false,
		isAddAccountAllowed: false,
		isAuditLogAllowed: false,
	};
	private _organizations: Organization[];
	private _sections: ISection[];
	private _section: ISection;
	public showNewHeader: boolean = true;

	headerItem: WebComponentWrapperOptions = {
		type: 'module',
		remoteEntry: this.appConfig.headerUrl ? this.appConfig.headerUrl + '/assets/remoteEntry.js' : '',
		exposedModule: './web-components',
		elementName: 'zesty-header',
	};

	sidebarItem: WebComponentWrapperOptions = {
		type: 'module',
		remoteEntry: this.appConfig.sidebarUrl ? this.appConfig.sidebarUrl + '/assets/remoteEntry.js' : '',
		exposedModule: './web-components',
		elementName: 'zesty-sidebar',
	};

	constructor(
		@Inject(APP_CONFIG)
		private appConfig: AppConfiguration,
		private organizationsService: OrganizationsService,
		private supportModalService: SupportModalService,
		private accountService: AccountService,
		private permissions: PermissionService,
		private userService: UserService,
		private router: Router,
		public featureFlagManagerService: FeatureFlagManagerService,
	) {
		super();
		this.showNewHeader = this.appConfig.customerType !== CustomerType.BO;
	}

	public ngOnInit(): void {
		this.initSections();
		this.fetchAccounts();
		this.getOrganizations();
		this.detectActiveSection();
		this.subscribeToRouteChanges();
		this.supportModalService.init();

		window.HostRouter = this.router;
	}

	public onSidebarSizeChanged(isCollapsed: boolean): void {
		this._isSidebarCollapsed = isCollapsed;
	}

	public get restrictions(): ISidebarRestrictions {
		return this._restrictions;
	}

	public get isSidebarCollapsed(): boolean {
		return this._isSidebarCollapsed;
	}

	public get organizations(): Organization[] {
		return this._organizations;
	}

	public get sections(): ISection[] {
		return this._sections;
	}

	public get section(): ISection {
		return this._section;
	}

	private initSections(): void {
		this._sections = sidebarSections.map((section) => ({
			...section,
			isAllowed: this.permissions.isPermitted(section.permissionKey),
			chapters: this.initChapters(section.chapters),
		}));

		if (this.appConfig.customerType === CustomerType.BO) {
			this._sections = this._sections.filter((section) => section.permissionKey === CONTROL_PANEL_KEY);
		} else {
			//hide control panel from zesty app
			this._sections = this._sections.filter((section) => section.permissionKey !== CONTROL_PANEL_KEY);
		}
	}

	private initChapters(chapters: ISectionChapter[]): ISectionChapter[] {
		return chapters.map((chapter) => {
			const pages: ISectionPage[] = this.createPagesForChapter(chapter);
			const isAllowed = this.permissions.isPermitted(chapter.permissionKey) && pages.some((page) => page.isAllowed);

			return {
				...chapter,
				isAllowed,
				pages,
			};
		});
	}

	private createPagesForChapter(chapter: ISectionChapter): ISectionPage[] {
		return chapter.pages.reduce((pages: ISectionPage[], page: ISectionPage) => {
			const isFlagEnabled = !!page.featureFlag && this.featureFlagManagerService.isFlagEnabled(page.featureFlag);
			const isCloudTypeMatch = page.orgCloudTypes.some(
				(orgType) => orgType === this.userService.organization.org_cloud_type,
			);
			const isAvailable = (!page.featureFlag || isFlagEnabled) && isCloudTypeMatch;

			const isAllowed =
				(page.permissionKey !== Permissions.DatabaseExplorerView && this.permissions.isPermitted(page.permissionKey)) ||
				(page.permissionKey === Permissions.DatabaseExplorerView &&
					this.permissions.isPermitted(page.permissionKey) &&
					this.userService.organization.cur_loaded);

			if (isAvailable) {
				pages.push({
					...page,
					isAllowed,
					name: page.name,
				});
			}
			return pages;
		}, []);
	}

	private getOrganizations(): void {
		this.organizationsService
			.getOrganizations()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((organizations) => {
				// If Azure VM FF is off => filter out related organizations
				if (this.enableOptimizationHub) {
					this._organizations = organizations;
				} else {
					this._organizations = organizations.filter((item) => item.org_cloud_type !== OrganizationTypes.Azure);
				}
				const currentOrganization: Organization = getActiveOrganization(organizations);
				this.userService.updateUserOrganization(currentOrganization);
			});
	}

	private fetchAccounts(): void {
		this.accountService.loadAccounts().pipe(takeUntil(this.unsubscribe)).subscribe();
	}

	private subscribeToRouteChanges(): void {
		this.router.events.pipe(takeUntil(this.unsubscribe)).subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.detectActiveSection(event);
			}
		});
	}

	private validateElementsPermissions(): void {
		this._restrictions = {
			isNotificationsAllowed: this.permissions.isPermitted('notification-management'),
			isPresentationModeAllowed: this.permissions.isPermitted('present-mode'),
			isAuditLogAllowed: this.permissions.isPermitted('audit-log'),
			isOrganizationPickerAllowed: true,
			isAddAccountAllowed: true,
		};
		this._section.restrictions.forEach((restriction) => {
			if (!restriction.isAllowed) {
				this._restrictions[restriction.restrictionKey] = false;
			}
		});
	}

	private detectActiveSection(routerEventNavigationEnd?: NavigationEnd): void {
		const activeRoute = routerEventNavigationEnd?.urlAfterRedirects || location.pathname;
		this._section = this._sections.find((section) => {
			return !!section.chapters.find((chapter) => {
				if (chapter.type === SectionChapterEnum.Link) {
					return chapter.url.endsWith(activeRoute);
				}
				return !!chapter.pages.find((page) => page.url.endsWith(activeRoute));
			});
		});
		if (!this._section) {
			this._section = this._sections[0];
		}
		this.validateElementsPermissions();
	}
}
